import {
  Decimal,
  typedDiscountNeedsApproval,
  resolveCashierDiscountMode,
  type PosCustomFeeCalc,
  type PosOfflineDirectory,
  type PosOfflineSale,
} from '@mlm/shared';
import type {
  ActiveSlot,
  Branch,
  MemberSearchResult,
  PosCheckoutPayload,
  PosCheckoutResult,
  PosPriceMap,
  PosProduct,
  PreviewResult,
} from '../api';
import { checkoutTotals } from '../checkout-totals';

/**
 * The offline till's decisions, as pure functions over what is on the device.
 * Nothing here touches the network or storage, so all of it is unit-tested.
 */

type DirectoryMember = PosOfflineDirectory['members'][number];

/** More than any shop sells in one go, without looking like a real count. */
const NO_LIMIT = 999_999;

/**
 * Offline, the count on the device may be out of date, and the goods in the
 * cashier's hand are the truth: stock never stops a sale. So the caps are lifted
 * and the last known count moves to `offlineStock`, for display only.
 */
export function productsForOffline(products: PosProduct[]): PosProduct[] {
  return products.map((p) => ({
    ...p,
    offlineStock: p.offlineStock ?? p.sellableQty,
    sellableQty: NO_LIMIT,
    variants: p.variants.map((v) => ({
      ...v,
      offlineStock: v.offlineStock ?? v.stockQty,
      stockQty: NO_LIMIT,
      inStock: true,
    })),
  }));
}

/** Take a sale off the counts on the device, so the tiles keep up. */
export function takeFromStock(
  products: PosProduct[],
  items: Array<{ productId: string; variantId?: string; qty: number }>,
): PosProduct[] {
  return products.map((p) => {
    const lines = items.filter((i) => i.productId === p.id);
    if (lines.length === 0) return p;
    const qty = lines.reduce((n, l) => n + l.qty, 0);
    return {
      ...p,
      offlineStock: (p.offlineStock ?? p.sellableQty) - qty,
      variants: p.variants.map((v) => {
        const taken = lines.filter((l) => l.variantId === v.id).reduce((n, l) => n + l.qty, 0);
        return taken === 0 ? v : { ...v, offlineStock: (v.offlineStock ?? v.stockQty) - taken };
      }),
    };
  });
}

const RESULT_LIMIT = 20;

/** Search by name, slot code or referral code, like the online till minus contact details. */
export function searchDirectory(members: DirectoryMember[], query: string): MemberSearchResult[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const toUser = (m: DirectoryMember): MemberSearchResult => ({
    matchType: 'user',
    id: m.id,
    email: '',
    phone: null,
    fullName: m.fullName,
    referralCode: m.referralCode,
    slotId: null,
    slotCode: null,
  });
  const slotHits: MemberSearchResult[] = [];
  const userHits: MemberSearchResult[] = [];
  for (const m of members) {
    for (const s of m.slots) {
      if (s.slotCode.toLowerCase().startsWith(q)) {
        slotHits.push({ ...toUser(m), matchType: 'slot', slotId: s.id, slotCode: s.slotCode });
      }
    }
    const name = m.fullName?.toLowerCase() ?? '';
    if (name.includes(q) || m.referralCode?.toLowerCase() === q) userHits.push(toUser(m));
  }
  return [...slotHits.slice(0, 10), ...userHits.slice(0, RESULT_LIMIT)];
}

export function slotsOfMember(directory: PosOfflineDirectory, memberId: string): ActiveSlot[] {
  const member = directory.members.find((m) => m.id === memberId);
  return (member?.slots ?? []).map((s) => ({
    id: s.id,
    slotCode: s.slotCode,
    membershipName: s.membershipName,
    activatedAt: s.activatedAt,
    // Balances are never shown offline: a wallet can't be spent here anyway.
    available: '0.0000',
    availableGc: '0.0000',
    availableCoin: '0.0000',
  }));
}

const EMPTY_PRICES: PosPriceMap = { products: {}, variants: {} };

/** The member's price table, the same one the server would answer with. */
export function priceMapFor(
  directory: PosOfflineDirectory,
  memberId: string,
  slotId: string | null,
): PosPriceMap {
  const member = directory.members.find((m) => m.id === memberId);
  if (!member) return EMPTY_PRICES;
  const tableId = slotId
    ? (member.slots.find((s) => s.id === slotId)?.table ?? null)
    : member.noSlotTable;
  return (tableId ? directory.tables[tableId] : null) ?? EMPTY_PRICES;
}

type SaleItem = { productId: string; variantId?: string; qty: number };

/** The preview an online till would get, worked out on the device. */
export function localPreview(
  products: PosProduct[],
  prices: PosPriceMap,
  items: SaleItem[],
): PreviewResult {
  let subtotal = new Decimal(0);
  let discountTotal = new Decimal(0);
  let totalPv = new Decimal(0);
  const lines = items.map((item) => {
    const product = products.find((p) => p.id === item.productId);
    if (!product) throw new Error('offline_product_unknown');
    const variant = item.variantId
      ? product.variants.find((v) => v.id === item.variantId)
      : undefined;
    if (item.variantId && !variant) throw new Error('offline_product_unknown');
    const base = new Decimal(variant ? variant.price : product.basePrice);
    const overlay = variant ? prices.variants[variant.id] : prices.products[product.id];
    const unit = overlay ? new Decimal(overlay.unitPrice) : base;
    const pv = new Decimal(variant ? variant.pv : product.pv);
    subtotal = subtotal.plus(base.mul(item.qty));
    discountTotal = discountTotal.plus(base.minus(unit).mul(item.qty));
    totalPv = totalPv.plus(pv.mul(item.qty));
    return {
      productId: product.id,
      productName: variant ? `${product.name} — ${variant.name}` : product.name,
      productSku: variant?.sku ?? product.sku,
      qty: item.qty,
      unitPrice: unit.toFixed(4),
      unitPriceBase: base.toFixed(4),
      appliedDiscountPct: overlay?.appliedDiscountPct ?? '0.0000',
      unitPv: pv.toFixed(4),
    };
  });
  return {
    lines,
    subtotal: subtotal.toFixed(4),
    discountTotal: discountTotal.toFixed(4),
    total: subtotal.minus(discountTotal).toFixed(4),
    totalPv: totalPv.toFixed(4),
    currency: 'cash',
    managerDiscountPct: '0.0000',
    managerDiscountAmount: '0.0000',
    couponCode: null,
    couponTotal: '0.0000',
    couponLabel: null,
  };
}

/** A refusal the cashier can read, with the same `error` code shape as the API. */
export class OfflineRefusal extends Error {
  constructor(public readonly code: string) {
    super(code);
  }
}

/** Temporary receipt number. The final one is the branch's own, given at sync. */
export function localSaleNumber(deviceId: string, seq: number): string {
  const device = deviceId
    .replace(/[^a-z0-9]/gi, '')
    .slice(0, 4)
    .toUpperCase();
  return `OFF-${device}-${String(seq).padStart(4, '0')}`;
}

export type OfflineSaleContext = {
  payload: PosCheckoutPayload;
  directory: PosOfflineDirectory;
  products: PosProduct[];
  branch: Branch | undefined;
  cashier: { id: string; fullName: string | null; email: string; roles: string[] };
  deviceId: string;
  localNumber: string;
  now: Date;
};

/**
 * Turn what the checkout page submitted into a sale kept on this device, plus
 * the response the page expects from a real checkout. Anything that needs the
 * server to be sure about (a wallet, a code, a manager's approval) is refused.
 */
export function buildOfflineSale(ctx: OfflineSaleContext): {
  sale: PosOfflineSale;
  receipt: PosCheckoutResult;
} {
  const { payload, directory, branch, cashier } = ctx;
  if (payload.stockistTransfer) throw new OfflineRefusal('offline_stockist_transfer');
  if (payload.discountPresetId || payload.couponCode || payload.voucherId) {
    throw new OfflineRefusal('offline_coupon_not_available');
  }
  const idempotencyKey = payload.payment.idempotencyKey;
  if (!idempotencyKey) throw new OfflineRefusal('offline_sale_key_missing');

  const method = directory.methods.find((m) => m.code === payload.payment.paymentMethodCode);
  if (!method) throw new OfflineRefusal('offline_method_not_allowed');
  if (
    method.roleCodes.length > 0 &&
    !cashier.roles.includes('super_admin') &&
    !method.roleCodes.some((r) => cashier.roles.includes(r))
  ) {
    throw new OfflineRefusal('offline_method_not_allowed');
  }

  const member = directory.members.find((m) => m.id === payload.buyerUserId);
  if (!member) throw new OfflineRefusal('offline_member_unknown');
  const slotId = payload.buyerSlotId ?? null;

  // A typed discount above the branch's caps needs a manager, and a manager's
  // approval needs the server.
  const md = payload.managerDiscount;
  const pct = md?.pct ?? 0;
  const amount = md?.amount ?? 0;
  if (pct || amount) {
    const mode = resolveCashierDiscountMode(branch);
    if (!branch || mode === 'off') throw new OfflineRefusal('cashier_discount_disabled');
    const needsApproval = typedDiscountNeedsApproval({
      mode,
      pct,
      amount,
      maxPct: Number(branch.cashierDiscountMaxPct ?? 0),
      maxAmount: Number(branch.cashierDiscountMaxAmount ?? 0),
    });
    if (needsApproval) throw new OfflineRefusal('offline_manager_approval');
  }

  const preview = localPreview(
    ctx.products,
    priceMapFor(directory, member.id, slotId),
    payload.items,
  );

  // Fees in the method's own order: it is a cascade, so order changes the sum.
  const requested = payload.customFees ?? [];
  const fees = method.fees
    .filter((t) => requested.some((r) => r.typeCode === t.code))
    .map((t) => ({
      code: t.code,
      label: t.label,
      calc: t.calc as PosCustomFeeCalc,
      amount: requested.find((r) => r.typeCode === t.code)?.amount,
    }));
  if (fees.length !== requested.length) throw new OfflineRefusal('unknown_custom_fee_type');

  const totals = checkoutTotals({
    goodsTotal: preview.total,
    currency: 'cash',
    discountPct: String(pct),
    discountAmount: String(amount),
    fees,
  });

  const exact = method.tenderKind === 'exact';
  const tendered = exact ? totals.due : new Decimal(payload.payment.amountTendered);
  if (tendered.lt(totals.due)) throw new OfflineRefusal('insufficient_payment');
  const change = exact ? new Decimal(0) : tendered.minus(totals.due);

  const soldAt = ctx.now.toISOString();
  const sale: PosOfflineSale = {
    clientSaleId: idempotencyKey,
    deviceId: ctx.deviceId,
    localNumber: ctx.localNumber,
    soldAt,
    soldByUserId: cashier.id,
    branchId: payload.branchId,
    buyerUserId: payload.buyerUserId,
    buyerSlotId: slotId,
    items: preview.lines.map((line, i) => ({
      productId: line.productId,
      variantId: payload.items[i]!.variantId,
      qty: line.qty,
      unitPrice: line.unitPrice,
      unitPriceBase: line.unitPriceBase,
    })),
    payment: {
      methodCode: method.code,
      methodLabel: method.label,
      tenderKind: method.tenderKind,
      amountTendered: tendered.toFixed(4),
    },
    managerDiscount: pct || amount ? { pct, amount, reason: md?.reason } : undefined,
    customFees: totals.feeLines.map((f) => ({ code: f.code, label: f.label, amount: f.amount })),
    notes: payload.notes,
    total: totals.dueFixed,
  };

  const receipt: PosCheckoutResult = {
    offline: true,
    order: {
      id: idempotencyKey,
      code: ctx.localNumber,
      status: branch?.completionMode === 'manual' ? 'to_release' : 'completed',
      subtotal: preview.subtotal,
      discountTotal: preview.discountTotal,
      couponCode: null,
      couponTotal: '0.0000',
      couponLabel: null,
      managerDiscountAmount: totals.cashierDiscount.toFixed(4),
      total: totals.dueFixed,
      totalPv: preview.totalPv,
      customFeeLabel: totals.feeLines[0]?.label ?? null,
      customFeeAmount: totals.feeTotal.toFixed(4),
      customFees: totals.feeLines,
      branchId: payload.branchId,
      buyerUserId: payload.buyerUserId,
      buyerSlotId: slotId,
      notes: payload.notes ?? null,
      createdAt: soldAt,
    },
    payment: {
      id: idempotencyKey,
      method: exact ? 'card' : 'cash',
      methodLabel: method.label,
      amount: totals.dueFixed,
      amountTendered: tendered.toFixed(4),
      changeDue: change.toFixed(4),
      status: 'captured',
    },
    // Minted by the server when the sale syncs, into the buyer's Vault.
    kitCodes: [],
    items: preview.lines.map((l, i) => {
      const product = ctx.products.find((p) => p.id === l.productId);
      const variantId = payload.items[i]!.variantId;
      return {
        name: product?.name ?? l.productName,
        variantName: product?.variants.find((v) => v.id === variantId)?.name ?? null,
        qty: l.qty,
        unitPrice: new Decimal(l.unitPrice).toFixed(2),
        lineTotal: new Decimal(l.unitPrice).mul(l.qty).toFixed(2),
      };
    }),
    buyerName: member.fullName,
    cashierName: cashier.fullName ?? cashier.email,
    replayed: false,
  };
  return { sale, receipt };
}
